import React, { useState, useEffect, memo } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://v2.schmalgsicht.de';

/**
 * Section 0 - Hero/Title Section
 * Displays the main title and subtitle
 */
export const HeroSection = memo(function HeroSection({ scrollProgress }) {
  // Uptime counter
  const [uptime, setUptime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isStreamOnline, setIsStreamOnline] = useState(null); // null = loading, true/false = status
  const [onlineSince, setOnlineSince] = useState(null);

  // Check stream status via API
  useEffect(() => {
    const checkStreamStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/status`);
        if (response.ok) {
          const data = await response.json();
          setIsStreamOnline(data.stream_online === true);

          // Use timestamp from backend
          if (data.stream_online_since) {
            setOnlineSince(new Date(data.stream_online_since));
          } else {
            setOnlineSince(null);
          }
        } else {
          setIsStreamOnline(false);
          setOnlineSince(null);
        }
      } catch (error) {
        setIsStreamOnline(false);
        setOnlineSince(null);
      }
    };

    checkStreamStatus();
    const statusInterval = setInterval(checkStreamStatus, 30000); // Check every 30 seconds

    return () => clearInterval(statusInterval);
  }, []);

  // Update uptime counter every second
  useEffect(() => {
    if (!isStreamOnline || !onlineSince) {
      setUptime({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    const updateUptime = () => {
      const now = new Date();
      const diff = now - onlineSince;

      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      setUptime({ days, hours, minutes, seconds });
    };

    updateUptime();
    const interval = setInterval(updateUptime, 1000);

    return () => clearInterval(interval);
  }, [isStreamOnline, onlineSince]);

  const formatTime = (num) => String(num).padStart(2, '0');

  return (
    <div
      id="home"
      className="min-h-screen sm:h-screen w-full relative pointer-events-none flex items-center justify-center"
      style={{
        scrollSnapAlign: 'start',
        scrollSnapStop: 'always',
        overflow: 'hidden',
        maxWidth: '100vw'
      }}
    >
      <div
        className="w-full sm:max-w-5xl text-white transition-opacity duration-1000 relative z-10 px-4 sm:px-8 md:px-16 lg:px-24 flex flex-col items-center"
        style={{
          opacity: scrollProgress < 0.8 ? 1 : 0,
          maxWidth: 'min(95vw, 100%)'
        }}
      >
        <div className="w-full sm:inline-flex sm:flex-col sm:items-stretch sm:w-auto">
          <div className="flex flex-col sm:flex-row items-end sm:items-center justify-center gap-6 sm:gap-8 mb-6">
            {/* Links: schmal + gsicht untereinander */}
            <div className="flex flex-col leading-none text-right w-full sm:w-auto">
              <h1 className="text-[24vw] sm:text-8xl md:text-9xl lg:text-[10rem] xl:text-[12rem] font-bold tracking-tight" style={{ lineHeight: '0.85' }}>
                schmal
              </h1>
              <h1 className="text-[24vw] sm:text-8xl md:text-9xl lg:text-[10rem] xl:text-[12rem] font-bold tracking-tight" style={{ lineHeight: '0.85' }}>
                gs<span className="text-pink-500">ich</span>t
              </h1>
            </div>

            {/* Rechts: Gradient-Quadrat mit v2 */}
            <div className="bg-gradient-to-br from-blue-500 to-pink-500 rounded-2xl sm:rounded-[2rem] flex items-center justify-center w-full sm:w-auto sm:aspect-square py-2 sm:py-0 sm:h-[10rem] md:h-[12rem] lg:h-[16rem] xl:h-[20rem]">
              <span className="text-[12vw] sm:text-8xl md:text-9xl lg:text-[10rem] xl:text-[12rem] font-bold text-white">
                v2
              </span>
            </div>
          </div>

        </div>
        <p className="text-sm sm:text-base md:text-lg text-white/60 font-medium leading-normal text-center w-full mt-4 sm:mt-0">
          powered by schmalsoft solutions
        </p>

        {/* Uptime Counter */}
        <div className="mt-8 sm:mt-12 text-center">
          {isStreamOnline === null ? (
            // Loading state
            <p className="text-xs sm:text-sm text-gray-400 uppercase tracking-wider">
              Prüfe Stream-Status...
            </p>
          ) : isStreamOnline ? (
            <>
              <p className="text-xs sm:text-sm text-gray-400 uppercase tracking-wider mb-2">
                Wir sind live seit
              </p>
              <div className="flex items-center justify-center gap-2 sm:gap-4">
                <div className="flex flex-col items-center">
                  <span className="text-2xl sm:text-4xl md:text-5xl font-bold text-white tabular-nums">
                    {formatTime(uptime.days)}
                  </span>
                  <span className="text-xs sm:text-sm text-gray-400">Tage</span>
                </div>
                <span className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-400">:</span>
                <div className="flex flex-col items-center">
                  <span className="text-2xl sm:text-4xl md:text-5xl font-bold text-white tabular-nums">
                    {formatTime(uptime.hours)}
                  </span>
                  <span className="text-xs sm:text-sm text-gray-400">Stunden</span>
                </div>
                <span className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-400">:</span>
                <div className="flex flex-col items-center">
                  <span className="text-2xl sm:text-4xl md:text-5xl font-bold text-white tabular-nums">
                    {formatTime(uptime.minutes)}
                  </span>
                  <span className="text-xs sm:text-sm text-gray-400">Minuten</span>
                </div>
                <span className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-400">:</span>
                <div className="flex flex-col items-center">
                  <span className="text-2xl sm:text-4xl md:text-5xl font-bold text-white tabular-nums">
                    {formatTime(uptime.seconds)}
                  </span>
                  <span className="text-xs sm:text-sm text-gray-400">Sekunden</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs sm:text-sm text-gray-400 uppercase tracking-wider mb-2">
                Stream Status
              </p>
              <div className="flex items-center justify-center gap-3">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-2xl sm:text-4xl md:text-5xl font-bold text-red-400">
                  Offline
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
});
